import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { dashboardService, PredictionHistoryPayload, PredictionHistoryRow } from '../../services/DashboardService';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import styles from './PredictionAccuracy.module.css';

const PredictionAccuracy: React.FC = () => {
  const [data, setData] = useState<PredictionHistoryPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const d = await dashboardService.getPredictionHistory();
      setData(d);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load prediction history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 300_000); // 5 min
    return () => clearInterval(interval);
  }, [fetchData]);

  // Compute rolling accuracy data for the chart
  const rollingData = useMemo(() => {
    if (!data) return [];
    const scored = data.predictions.filter((r) => r.correct === 'YES' || r.correct === 'NO' || r.correct === 'PARTIAL');
    const points: { date: string; accuracy: number; cumCorrect: number; cumTotal: number }[] = [];
    let cumCorrect = 0;
    let cumTotal = 0;
    for (const r of scored) {
      cumTotal++;
      if (r.correct === 'YES') cumCorrect++;
      points.push({
        date: r.date,
        accuracy: Math.round((cumCorrect / cumTotal) * 1000) / 10,
        cumCorrect,
        cumTotal,
      });
    }
    return points;
  }, [data]);

  if (loading) return <SignalLoader message="Loading prediction data..." fullPage />;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return <div className={styles.loading}>No prediction data available</div>;

  const acc = data.accuracy;

  const dirBadge = (dir: string) => {
    const d = dir.toUpperCase();
    if (d === 'BULL') return <span className={`${styles.badge} ${styles.bullBadge}`}>Bull</span>;
    if (d === 'BEAR') return <span className={`${styles.badge} ${styles.bearBadge}`}>Bear</span>;
    return <span className={`${styles.badge} ${styles.neutralBadge}`}>Neutral</span>;
  };

  const correctClass = (c: string) => {
    if (c === 'YES') return styles.correct;
    if (c === 'NO') return styles.wrong;
    if (c === 'PARTIAL') return styles.partial;
    return '';
  };

  const scoredRows = data.predictions.filter(
    (r) => r.correct === 'YES' || r.correct === 'NO' || r.correct === 'PARTIAL',
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Prediction Accuracy</h1>
        <span className={styles.updated}>
          Updated: {data.updated_at ? new Date(data.updated_at).toLocaleString() : '—'}
        </span>
      </div>

      {/* Summary stat cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Total Scored</div>
          <div className={styles.statValue}>{acc.total}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Overall</div>
          <div className={`${styles.statValue} ${acc.accuracy_pct >= 50 ? styles.positive : styles.negative}`}>
            {acc.accuracy_pct}%
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>7-Day</div>
          <div className={`${styles.statValue} ${acc.accuracy_7d >= 50 ? styles.positive : styles.negative}`}>
            {acc.accuracy_7d}%
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>30-Day</div>
          <div className={`${styles.statValue} ${acc.accuracy_30d >= 50 ? styles.positive : styles.negative}`}>
            {acc.accuracy_30d}%
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Streak</div>
          <div className={`${styles.statValue} ${acc.streak_type === 'correct' ? styles.positive : styles.negative}`}>
            {acc.current_streak} {acc.streak_type === 'correct' ? 'W' : 'L'}
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Record</div>
          <div className={styles.statValue} style={{ fontSize: 16 }}>
            <span className={styles.positive}>{acc.correct}W</span> /{' '}
            <span className={styles.negative}>{acc.wrong}L</span>
            {acc.partial > 0 && (
              <>
                {' '}
                / <span className={styles.neutral}>{acc.partial}P</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Direction breakdown */}
      <h2 className={styles.sectionTitle}>Direction Breakdown</h2>
      <div className={styles.dirGrid}>
        <div className={styles.dirCard}>
          <div className={`${styles.dirLabel} ${styles.bullLabel}`}>Bull Predictions</div>
          <div className={`${styles.dirPct} ${acc.bull_accuracy_pct >= 50 ? styles.positive : styles.negative}`}>
            {acc.bull_accuracy_pct}%
          </div>
          <div className={styles.dirDetail}>
            {acc.bull_correct} / {acc.bull_total} correct
          </div>
        </div>
        <div className={styles.dirCard}>
          <div className={`${styles.dirLabel} ${styles.bearLabel}`}>Bear Predictions</div>
          <div className={`${styles.dirPct} ${acc.bear_accuracy_pct >= 50 ? styles.positive : styles.negative}`}>
            {acc.bear_accuracy_pct}%
          </div>
          <div className={styles.dirDetail}>
            {acc.bear_correct} / {acc.bear_total} correct
          </div>
        </div>
      </div>

      {/* Confidence calibration */}
      {acc.calibration && Object.keys(acc.calibration).length > 0 && (
        <>
          <h2 className={styles.sectionTitle}>Confidence Calibration</h2>
          <div className={styles.calGrid}>
            {(['low', 'medium', 'high'] as const).map((level) => {
              const c = acc.calibration[level];
              if (!c)
                return (
                  <div className={styles.calCard} key={level}>
                    <div className={styles.calLevel}>{level} Conf</div>
                    <div className={styles.calPct}>—</div>
                    <div className={styles.calCount}>no data</div>
                  </div>
                );
              return (
                <div className={styles.calCard} key={level}>
                  <div className={styles.calLevel}>{level} Confidence</div>
                  <div className={`${styles.calPct} ${c.accuracy_pct >= 50 ? styles.positive : styles.negative}`}>
                    {c.accuracy_pct}%
                  </div>
                  <div className={styles.calCount}>
                    {c.correct} / {c.total} correct
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Rolling accuracy chart */}
      {rollingData.length > 2 && (
        <>
          <h2 className={styles.sectionTitle}>Cumulative Accuracy Over Time</h2>
          <div className={styles.chartWrap}>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={rollingData}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                <ReferenceLine y={50} stroke="var(--text-muted)" strokeDasharray="4 4" />
                <Tooltip
                  formatter={(v: number) => [`${v}%`, 'Accuracy']}
                  contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-primary)' }}
                />
                <Line
                  type="monotone"
                  dataKey="accuracy"
                  stroke="var(--color-purple)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* Detailed history table */}
      <h2 className={styles.sectionTitle}>Prediction History</h2>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Predicted</th>
              <th>Confidence</th>
              <th>Score</th>
              <th>SPY Open</th>
              <th>SPY Close</th>
              <th>Actual</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {[...scoredRows].reverse().map((r: PredictionHistoryRow, i: number) => (
              <tr key={i}>
                <td>{r.date}</td>
                <td>{dirBadge(r.direction)}</td>
                <td>{r.confidence ? `${(parseFloat(r.confidence) * 100).toFixed(0)}%` : '—'}</td>
                <td>{r.raw_score ? parseFloat(r.raw_score).toFixed(1) : '—'}</td>
                <td>{r.spy_open ? `$${parseFloat(r.spy_open).toFixed(2)}` : '—'}</td>
                <td>{r.spy_close ? `$${parseFloat(r.spy_close).toFixed(2)}` : '—'}</td>
                <td>{r.actual_direction ? dirBadge(r.actual_direction) : '—'}</td>
                <td className={correctClass(r.correct)}>
                  {r.correct === 'YES'
                    ? 'Correct'
                    : r.correct === 'NO'
                      ? 'Wrong'
                      : r.correct === 'PARTIAL'
                        ? 'Partial'
                        : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PredictionAccuracy;
export { PredictionAccuracy };
